import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, from, merge, of } from 'rxjs';
import { catchError, delay, filter, groupBy, map, mergeMap, tap, throttleTime, switchMap } from 'rxjs/operators';
import { bufferDebounceTime } from 'common/rxjs/operators';
import { chain } from 'lodash';
import { messageConversationChanged, messageConversationsLoaded } from '../actions/message-conversation.actions';
import {
  bulkLoadMessages,
  loadMessages,
  messagesLoaded,
  messagesLoadFailed,
  sendMessage,
  sendMessageFailed,
  sendMessageSuccess,
  sendTypingIndicator,
  sendTypingIndicatorFailed,
  sendTypingIndicatorSuccess
} from '../actions/message.actions';
import {
  selectDeploymentIdByConversationId,
  selectExtendedMessagesByConversationId,
  selectMessageIdsByConversationId
} from '../selectors/message.selectors';
import { selectShowAgentTypingIndicator } from 'common/store/genesys-cloud/conversations/web-deployments/selectors/configuration.selectors';
import { MessageConversationApiService } from '../services/message-conversation-api.service';
import {
  selectCommunicationId,
  selectMessageConversationById
} from '../selectors/message-conversation.selectors';
import { ParticipantPurpose } from 'common/store/genesys-cloud/conversations/common/models/participant-purpose.enum';
import { loadUserDetails } from 'common/store/genesys-cloud/users/actions/user.actions';
import { Participant } from 'common/store/genesys-cloud/conversations/common/models/participant.model';
import { MessageDataSenderType } from '../../common/models/message-data-sender-type.enum';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudMessageEffects {
  static readonly MessageBulkFetchBufferTimeMillis = 500;
  static readonly MessageBulkFetchMaxItemsPerRequest = 200;
  static readonly MessageBulkFetchMaxRetryCount = 2;
  static readonly MessageBulkFetchRetryDelayMillis = 3000;
  private readonly pendingMessageIds = new Set<string>();
  private readonly allowedParticipants = new Set<ParticipantPurpose>([
    ParticipantPurpose.Customer,
    ParticipantPurpose.Workflow,
    ParticipantPurpose.Agent
  ]);

  private readonly initialConversations$ = this.actions$.pipe(
    ofType(messageConversationsLoaded),
    mergeMap(({ conversations }) => from(conversations))
  );
  private readonly changedConversations$ = this.actions$.pipe(
    ofType(messageConversationChanged),
    map(({ conversation }) => conversation)
  );

  private readonly _showAgentTypingIndicator$ = (conversationId) =>
    this.store
      .select(selectDeploymentIdByConversationId(conversationId))
      .pipe(switchMap((deploymentId) => this.store.select(selectShowAgentTypingIndicator(deploymentId))));

  readonly loadUserDetails$ = createEffect(() =>
    merge(this.initialConversations$, this.changedConversations$).pipe(
      concatLatestFrom((conversation) => this.store.select(selectMessageConversationById(conversation.id))),
      map(([conversation]) => {
        return chain(conversation.participants)
          .filter((participant) => this.filterOtherAgents(participant, conversation.agentParticipant.id))
          .map((participant) => participant.userId)
          .value();
      }),
      filter(([userIds]) => !!userIds),
      mergeMap((userIds) => from(userIds)),
      map((userId) => loadUserDetails({ userId }))
    )
  );

  readonly loadNewMessage$ = createEffect(() =>
    merge(this.initialConversations$, this.changedConversations$).pipe(
      concatLatestFrom((conversation) => this.store.select(selectMessageIdsByConversationId(conversation.id))),
      mergeMap(([conversation, fetchedMessageIds]) => {
        const messageIds = chain(conversation.participants)
          .filter((participant) => this.allowedParticipants.has(participant.purpose))
          .flatMap((participant) => participant.messages ?? [])
          .flatMap((participantMessage) => participantMessage.messages ?? [])
          .map((message) => message.messageId)
          .filter((messageId) => !this.pendingMessageIds.has(messageId))
          .filter((messageId) => !fetchedMessageIds.includes(messageId))
          .value();
        return messageIds.length > 0 ? of(loadMessages({ conversationId: conversation.id, messageIds })) : EMPTY;
      })
    )
  );

  readonly collectGroupedMessagesToBulk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMessages),
      groupBy(({ conversationId }) => conversationId),
      mergeMap((group$) =>
        group$.pipe(
          bufferDebounceTime(GenesysCloudMessageEffects.MessageBulkFetchBufferTimeMillis),
          mergeMap((actions) => {
            const groupedChunks = chain(actions)
              .flatMap((action) => action.messageIds)
              .uniq()
              .chunk(GenesysCloudMessageEffects.MessageBulkFetchMaxItemsPerRequest)
              .map((messageIds) => ({ conversationId: group$.key, messageIds }))
              .value();
            return from(groupedChunks);
          })
        )
      ),
      map((grouped) => bulkLoadMessages({ ...grouped, retryCount: 0 }))
    )
  );

  readonly fetchBulkMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkLoadMessages),
      mergeMap(({ conversationId, messageIds, retryCount }) => {
        this.registerPendingMessageIds(messageIds);

        return this.api.getMessages$(conversationId, messageIds).pipe(
          map((messages) => messagesLoaded({ conversationId, messages })),
          catchError(() => of(messagesLoadFailed({ conversationId, messageIds, retryCount }))),
          tap(() => this.deregisterPendingMessageIds(messageIds))
        );
      })
    )
  );

  readonly retryBulkMessageFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(messagesLoadFailed),
      delay(GenesysCloudMessageEffects.MessageBulkFetchRetryDelayMillis),
      filter(({ retryCount }) => retryCount <= GenesysCloudMessageEffects.MessageBulkFetchMaxRetryCount),
      map(({ conversationId, messageIds, retryCount }) =>
        bulkLoadMessages({ conversationId, messageIds, retryCount: retryCount + 1 })
      )
    )
  );

  readonly sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendMessage),
      concatLatestFrom(({ conversationId }) => this.store.select(selectCommunicationId(conversationId))),
      mergeMap(([{ conversationId, textBody }, communicationId]) =>
        this.api.sendMessage$(conversationId, communicationId, textBody).pipe(
          map((message) => sendMessageSuccess({ conversationId, message })),
          catchError((error: any) => of(sendMessageFailed({ conversationId, error })))
        )
      )
    )
  );

  readonly sendTypingIndicator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendTypingIndicator),
      concatLatestFrom(({ conversationId }) => this._showAgentTypingIndicator$(conversationId)),
      filter(([_, showAgentTypingIndicator]) => showAgentTypingIndicator),
      throttleTime(5000),
      concatLatestFrom(([{ conversationId }]) => this.store.select(selectCommunicationId(conversationId))),
      mergeMap(([[{ conversationId }], communicationId]) =>
        this.api.typing$(conversationId, communicationId).pipe(
          map(() => sendTypingIndicatorSuccess({ conversationId })),
          catchError((error: any) => of(sendTypingIndicatorFailed({ conversationId, error })))
        )
      )
    )
  );

  readonly refreshLoadedMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(messageConversationChanged),
      concatLatestFrom(({ conversation }) =>
        this.store.select(selectExtendedMessagesByConversationId(conversation.id))
      ),
      map(([{ conversation }, messages]) => {
        const messagesWithoutFinalReceipt = messages
          .filter(
            (m) =>
              m.senderType === MessageDataSenderType.ActualAgent &&
              (!m.normalizedReceipts || !m.normalizedReceipts[0].isFinalReceipt)
          )
          .map((m) => m.id);

        return { conversationId: conversation.id, messageIds: messagesWithoutFinalReceipt };
      }),
      map(({ conversationId, messageIds }) => loadMessages({ conversationId, messageIds }))
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly api: MessageConversationApiService
  ) {}

  registerPendingMessageIds(messageIds: string[]): void {
    messageIds.forEach((messageId) => this.pendingMessageIds.add(messageId));
  }

  deregisterPendingMessageIds(messageIds: string[]): void {
    messageIds.forEach((messageId) => this.pendingMessageIds.delete(messageId));
  }

  filterOtherAgents(participant: Participant, agentParticipantId: string) {
    return participant.purpose === ParticipantPurpose.Agent && participant.id !== agentParticipantId;
  }
}
